import { acoesDisponiveis, cadeiaDeIds, componenteDidatico, nomeDe } from '../dominio/didatico/catalogo'
import { CLASSES_ACO } from '../dominio/didatico/classesAco'
import { rotuloAcao, type AcaoPeca, type EstadoPeca } from '../dominio/didatico/estadoPecas'

const ROTULO_ESTADO: Record<EstadoPeca, string> = {
  normal: 'normal',
  destacada: 'destacada',
  transparente: 'transparente',
  aberta: 'aberta',
  afastada: 'afastada',
  removida: 'oculta/removida',
}

const ROTULO_CATEGORIA: Record<string, string> = {
  estrutura: 'Estrutura',
  zonaDeformacao: 'Zona de absorção',
  srs: 'SRS',
  retencao: 'Retenção',
  carroceria: 'Carroceria',
  habitaculo: 'Interior',
  mecanica: 'Mecânica',
  energia: 'Energia',
  vitima: 'Vítima',
}

interface Props {
  id: string | null
  estado: EstadoPeca
  aoExecutar: (id: string, acao: AcaoPeca) => void
  aoSelecionar: (id: string) => void
  aoFocar?: (id: string) => void
  aoLimpar?: () => void
}

/**
 * Painel de detalhes da peça selecionada.
 *
 * Mostra apenas as ações pertinentes à peça: uma dobradiça não oferece "abrir",
 * e um vidro não oferece "explodir".
 */
export function PainelDetalhes({ id, estado, aoExecutar, aoSelecionar, aoFocar, aoLimpar }: Props) {
  if (!id) {
    return (
      <div className="detalhes vazio">
        <span className="titulo-controle">Peça</span>
        <p>
          Toque ou clique em uma peça da maquete para ver nome, descrição e ações. O primeiro toque
          seleciona o conjunto; o toque seguinte entra nas peças internas.
        </p>
      </div>
    )
  }

  const componente = componenteDidatico(id)
  const acoes = acoesDisponiveis(id)
  const cadeia = cadeiaDeIds(id)

  return (
    <div className="detalhes">
      <span className="titulo-controle">Peça selecionada</span>
      {cadeia.length > 1 && (
        <nav className="trilha" aria-label="Conjunto da peça">
          {cadeia.map((passo, i) => (
            <span key={passo}>
              {i > 0 && <span aria-hidden="true"> › </span>}
              {passo === id ? (
                <strong>{nomeDe(passo).split('—').pop()!.trim()}</strong>
              ) : (
                <button className="ligacao" onClick={() => aoSelecionar(passo)}>
                  {nomeDe(passo)}
                </button>
              )}
            </span>
          ))}
        </nav>
      )}
      <h2>{componente?.nome ?? id}</h2>
      <div className="linha-etiquetas">
        {componente && (
          <span className="etiqueta" style={{ borderColor: componente.corSemantica, color: componente.corSemantica }}>
            {ROTULO_CATEGORIA[componente.categoria] ?? componente.categoria}
          </span>
        )}
        {componente?.sigla && <span className="etiqueta">{componente.sigla}</span>}
        <span className="etiqueta estado">{ROTULO_ESTADO[estado]}</span>
      </div>

      {(aoFocar || aoLimpar) && (
        <div className="acoes-navegacao-peca" role="group" aria-label="Ações de visualização da peça">
          {aoFocar && (
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--secundario"
              onClick={() => aoFocar(id)}
              title="Enquadrar a câmera na peça selecionada"
              style={{ flex: 1, padding: '0.35rem 0.6rem', fontSize: '0.78rem' }}
            >
              Focar peça
            </button>
          )}
          {aoLimpar && (
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--secundario"
              onClick={aoLimpar}
              title="Limpar seleção e contorno"
              style={{ flex: 1, padding: '0.35rem 0.6rem', fontSize: '0.78rem' }}
            >
              Limpar seleção
            </button>
          )}
        </div>
      )}

      <div className="secao-didatica-detalhe">
        <span className="subtitulo-detalhe">O que é</span>
        <p>{componente?.descricao ?? 'Sem descrição didática cadastrada para esta peça.'}</p>
      </div>

      {componente?.funcao && (
        <div className="secao-didatica-detalhe">
          <span className="subtitulo-detalhe">Função</span>
          <p>{componente.funcao}</p>
        </div>
      )}

      {componente?.material && (
        <p className="informacao-anatomia"><strong>Material / construção</strong>{componente.material}</p>
      )}
      {componente?.classeAco && (
        <p className="informacao-anatomia">
          <strong>Classe de aço provável</strong>
          {CLASSES_ACO[componente.classeAco].nome} — {CLASSES_ACO[componente.classeAco].leituraOperacional}
        </p>
      )}
      {componente?.atencaoOperacional && (
        <p className="informacao-anatomia atencao"><strong>Atenção no resgate</strong>{componente.atencaoOperacional}</p>
      )}
      {componente?.confirmarNaFicha && (
        <p className="confirmar-ficha">Confirme localização, material e reforços na ficha de salvamento do modelo.</p>
      )}
      <div className="grade-acoes">
        {acoes.map((acao) => (
          <button key={acao} onClick={() => aoExecutar(id, acao)} className={acao === 'restaurar' ? '' : 'acao'}>
            {rotuloAcao(acao)}
          </button>
        ))}
      </div>
    </div>
  )
}
